import { findGroupById, getGroupsByTenantId } from '../mock-data/groups.mock';
import {
  createMockResponse,
  createMockErrorResponse,
} from '../mock.interceptor';

import type {
  GroupApiResponse,
  GroupListApiResponse,
} from '../../features/groups/groups.models';
import type { HttpResponse } from '@angular/common/http';
import type { Observable } from 'rxjs';

/**
 * /groups* へのリクエストをハンドリング
 * path はテナントプレフィックス除去済みの短縮パス（例: /groups, /groups/:id）
 */
export function handleGroupsRequest(
  method: string,
  path: string,
  _body: unknown,
  tenantId: string,
  searchParams: URLSearchParams,
): Observable<HttpResponse<unknown>> | null {
  // GET /groups - グループ一覧取得
  if (method === 'GET' && path === '/groups') {
    return handleGetGroups(tenantId, searchParams);
  }

  // GET /groups/:id - グループ詳細取得
  const getDetailMatch = /^\/groups\/([^/]+)$/.exec(path);
  if (method === 'GET' && getDetailMatch) {
    const groupId = getDetailMatch[1];
    return handleGetGroupDetail(groupId, tenantId);
  }

  return null;
}

// API 仕様に合わせた cursor pagination の制限値
// （apps/api-server/src/infrastructure/routes/v0/schemas.ts 170-174 参照）
const DEFAULT_LIMIT = 20;
const MIN_LIMIT = 1;
const MAX_LIMIT = 100;

/**
 * GET /api/v0/tenants/:tenantId/groups - グループ一覧取得
 * cursor pagination に対応:
 *   - limit: 1〜100（デフォルト 20）
 *   - cursor: 前ページ末尾グループの publicId
 *   - parentId: 親グループ ID（クエリ未指定時は全件、'null' 文字列指定時はルート相当）
 *     mock では実装簡略化のためクエリは無視し、テナント配下の全グループを cursor/limit のみで分割する
 */
function handleGetGroups(
  tenantId: string,
  searchParams: URLSearchParams,
): Observable<HttpResponse<GroupListApiResponse>> {
  const groups = getGroupsByTenantId(tenantId);

  // limit の解釈（範囲外・不正値は DEFAULT_LIMIT にフォールバック）
  const limitParam = searchParams.get('limit');
  const parsedLimit =
    limitParam !== null ? Number.parseInt(limitParam, 10) : Number.NaN;
  const limit = Number.isFinite(parsedLimit)
    ? Math.min(Math.max(parsedLimit, MIN_LIMIT), MAX_LIMIT)
    : DEFAULT_LIMIT;

  // cursor の解釈（指定があれば該当 publicId の次の要素から開始）
  const cursor = searchParams.get('cursor');
  const startIndex =
    cursor !== null
      ? Math.max(
          groups.findIndex((g) => g.publicId === cursor),
          -1,
        ) + 1
      : 0;

  const items = groups.slice(startIndex, startIndex + limit);
  const hasNext = startIndex + limit < groups.length;
  const nextCursor =
    hasNext && items.length > 0 ? items[items.length - 1].publicId : null;

  return createMockResponse<GroupListApiResponse>({
    items,
    nextCursor,
  });
}

/**
 * GET /api/v0/tenants/:tenantId/groups/:id - グループ詳細取得（グループ情報のみ）
 */
function handleGetGroupDetail(
  groupId: string,
  tenantId: string,
): Observable<HttpResponse<GroupApiResponse | { message: string }>> {
  const group = findGroupById(groupId, tenantId);
  if (group) {
    return createMockResponse<GroupApiResponse>(group);
  }

  return createMockErrorResponse('グループが見つかりません', 404);
}
